/**
 * The brand the "Your brand, already applied" row reads: granola.ai, real.
 *
 * ── WHY A REAL ONE, AFTER DECIDING NOT TO ────────────────────────────────────────────────────
 *
 * This file replaces `norwoodKit.ts`, an invented studio written on the rule that "a real company's
 * identity has no business on our landing page". Beka reversed that (2026-09-12), and the reversal
 * has a precedent already shipping: `FeaturesSection/WebImportDemo` is a landing animation built on
 * this same capture — granola.ai's screenshot, mark, palette, faces and page pictures, taken off the
 * live site with Playwright into `public/landing/features/web-demo`. Two rows on one page showing
 * two different brands for the same feature would have been the odd thing.
 *
 * So every value below is READ rather than designed, and this file is a pointer to that capture
 * rather than a second copy of it — the numbers are `WebImportDemo`'s `BRAND` verbatim.
 *
 * WHAT IS DELIBERATELY NOT USED: the 24 scraped page images include team headshots and third-party
 * product logos. Neither belongs on our marketing page, whatever the rest of the capture is doing —
 * so `GRANOLA_IMAGES` is the three of granola's own art-directed pictures, and nothing with a face
 * or someone else's mark in it.
 */

const ASSETS = '/landing/features/web-demo';

export const GRANOLA = {
  name: 'Granola',
  host: 'granola.ai',
  title: 'The AI notepad for back-to-back meetings',
  /** The whole home page, 3200x2500, as captured. Cropped to its hero wherever it is drawn small. */
  screenshot: `${ASSETS}/granola.png`,
  /**
   * The mark: the spiral on its square lime plate, 400 square, supplied by Beka (2026-09-15). The
   * capture's own `logo.png` is a 48px favicon and drew soft at the kit tile's 64. That file stays,
   * because `WebImportDemo` still uses it.
   */
  logo: `${ASSETS}/granola-logo.png`,

  /* The palette, off the RENDERED page — computed colours weighted by painted area, not picked. */
  paper: '#fcfcf8',
  surface: '#f7f7f2',
  ink: '#0e0f0c',
  inkMuted: '#72726e',
  accent: '#b2c248',
  line: '#dedede',
} as const;

/**
 * Five, accent first.
 *
 * The order is the tile's: the first block takes the full-height half, and that has to be the colour
 * that IDENTIFIES the brand — granola's lime, which is the only thing in this palette you could pick
 * out of a line-up. The two off-whites are genuinely a pair four points apart; they are shown as
 * such rather than tidied into one, because that pair is what the page is actually built on.
 */
export const GRANOLA_COLORS: readonly { role: string; value: string }[] = [
  { role: 'Accent', value: GRANOLA.accent },
  { role: 'Background', value: GRANOLA.paper },
  { role: 'Text', value: GRANOLA.ink },
  { role: 'Surface', value: GRANOLA.surface },
  { role: 'Muted', value: GRANOLA.inkMuted },
];

/** The two faces the page actually sets. Names only — no webfont is pulled onto the landing page. */
export const GRANOLA_TYPE = { display: 'Quadrant', body: 'Melange' } as const;

/**
 * Three of granola's own pictures, the kind a kit keeps: art direction, not stock and not people.
 *
 * Portrait-ish on purpose — they stack into the bento's right-hand rail, which is the column the
 * panel's edge cuts, and a picture is the one thing that loses nothing by being cropped.
 */
export const GRANOLA_IMAGES: readonly { src: string; alt: string }[] = [
  { src: `${ASSETS}/green-poster.jpg`, alt: '' },
  { src: `${ASSETS}/explosion.jpg`, alt: '' },
  { src: `${ASSETS}/grey.jpg`, alt: '' },
];
